/**
 * Composer prefill (Tasks 191/192/196). The cross-cutting "seed the composer
 * with a starter question" concern shared by the Corpus Reader's "ask about
 * this" (191) and shared `?q=` links (192/196).
 *
 * This lives at the app-shell level (not inside the chat view) because the seed
 * must survive a /corpus → /chat navigation and is handed to <CorpusReader> as
 * `onAsk`. Extracted verbatim from App.tsx (Task 201); behavior is unchanged.
 *
 * `pendingAsk` is read ONLY as the composer's initial draft (via a key remount
 * on `askNonce`); it is consumed the moment any query is sent. Starting a NEW
 * conversation means the reader-origin prompt is an unambiguous standalone — it
 * can't poison the follow-up cursor (Task 170/186).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { parseSharedQuery, searchWithoutShareQuery } from "../share/share-query";

export interface ComposerPrefill {
  /** The draft text to seed the composer with, or null for an empty composer. */
  pendingAsk: string | null;
  /** Bumped on every seed so the composer remounts (key=) onto the new draft. */
  askNonce: number;
  /** True only when the current prefill came from a shared `?q=` link (badge). */
  prefillShared: boolean;
  /** Reader "ask about this": seed a fresh standalone draft (not a shared origin). */
  seedAsk: (prompt: string) => void;
  /** Drop any pending draft + shared-origin tracking (e.g. on "Nueva conversación"). */
  reset: () => void;
  /**
   * Consume the prefill on send: clears the draft + badge and, if the question
   * came from a shared link, returns whether it was sent as_is (unchanged) vs
   * edited so the first answer can be tagged for safe analytics (Task 196).
   */
  consumeShareOrigin: (query: string) => "as_is" | "edited" | undefined;
}

export function useComposerPrefill(): ComposerPrefill {
  const [pendingAsk, setPendingAsk] = useState<string | null>(null);
  const [askNonce, setAskNonce] = useState<number>(0);
  // Task 192: true only when the current prefill came from a shared `?q=` link
  // (drives the "Pregunta compartida" badge). Reader "ask about this" leaves it false.
  const [prefillShared, setPrefillShared] = useState<boolean>(false);
  // Task 196: the shared-link question text, so the first ask can be tagged
  // as_is vs edited for safe analytics. A ref (not state) → no stale closure in
  // sendQuery and no extra render. Consumed (nulled) on the first send.
  const sharedTextRef = useRef<string | null>(null);

  const seedAsk = useCallback((prompt: string): void => {
    setAskNonce((n) => n + 1);
    setPendingAsk(prompt);
    setPrefillShared(false);
    sharedTextRef.current = null; // reader-ask is not a shared-link origin
  }, []);

  const reset = useCallback((): void => {
    setPendingAsk(null);
    setPrefillShared(false);
    sharedTextRef.current = null;
  }, []);

  const consumeShareOrigin = useCallback(
    (query: string): "as_is" | "edited" | undefined => {
      setPendingAsk(null); // Task 191: a sent query consumes any reader-seeded draft
      setPrefillShared(false); // Task 192: clear the shared-question badge on send
      // Task 196: if this question came from a shared link, tag the FIRST send as
      // as_is (unchanged) vs edited, then consume the ref. Safe, coarse — no text stored.
      const sharedText = sharedTextRef.current;
      sharedTextRef.current = null;
      return sharedText !== null
        ? query.trim() === sharedText.trim()
          ? "as_is"
          : "edited"
        : undefined;
    },
    [],
  );

  // external: window.location — capture a shared `?q=` question ONCE on mount,
  // prefill the composer (no auto-submit, no thread write → no session pollution),
  // then strip `q` from the URL so a reload won't re-prefill. Task 192.
  useEffect(() => {
    const shared = parseSharedQuery(window.location.search);
    if (shared === null) return;
    setAskNonce((n) => n + 1);
    setPendingAsk(shared);
    setPrefillShared(true);
    sharedTextRef.current = shared; // Task 196: tag the first ask's origin
    const cleaned = searchWithoutShareQuery(window.location.search);
    window.history.replaceState(null, "", `${window.location.pathname}${cleaned}${window.location.hash}`);
  }, []);

  return { pendingAsk, askNonce, prefillShared, seedAsk, reset, consumeShareOrigin };
}
